/**
 * Tarifs & Coefficients – gestion des valeurs seedées (fond orange + bouton ✅)
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/ApiService';
import AuthContext from '../../contexts/AuthContext';
import { buildAuthHeaders } from '../../services/httpClient';

type TarifsKeys =
  | 'tarifHoraire'
  | 'tempsCoutureMetre'
  | 'tempsCoutureGlissiere'
  | 'prixFermetureGlissiere'
  | 'tauxTVAAchat'
  | 'priseGabaritForfait'
  | 'priseGabaritTarifM2'
  | 'majorationSurpiqurePaspoil'
  | 'majorationFormeComplexe'
  | 'tarifCoutureRenfortsBache'
  | 'tarifCoutureSanglesBache'
  | 'prixSangleML'
  | 'prixOeilletUnitaire'
  | 'tempsPoseOeillet'
  | 'majorationAssemblageBache'
  | 'tempsAssemblageBacheM2'
  | 'tempsPreparationBase'
  | 'tempsPreparationParM2'
  | 'tempsPreparationFermeture'
  | 'tempsPreparationMousse'
  | 'tempsPreparationMatDifferentes';

type TarifsFormData = Record<TarifsKeys, number>;

const TARIFS_KEYS: TarifsKeys[] = [
  'tarifHoraire',
  'tempsCoutureMetre',
  'tempsCoutureGlissiere',
  'prixFermetureGlissiere',
  'tauxTVAAchat',
  'priseGabaritForfait',
  'priseGabaritTarifM2',
  'majorationSurpiqurePaspoil',
  'majorationFormeComplexe',
  'tarifCoutureRenfortsBache',
  'tarifCoutureSanglesBache',
  'prixSangleML',
  'prixOeilletUnitaire',
  'tempsPoseOeillet',
  'majorationAssemblageBache',
  'tempsAssemblageBacheM2',
  'tempsPreparationBase',
  'tempsPreparationParM2',
  'tempsPreparationFermeture',
  'tempsPreparationMousse',
  'tempsPreparationMatDifferentes',
];

const defaultFormData: TarifsFormData = {
  tarifHoraire: 0,
  tempsCoutureMetre: 0,
  tempsCoutureGlissiere: 0,
  prixFermetureGlissiere: 0,
  tauxTVAAchat: 20,
  priseGabaritForfait: 0,
  priseGabaritTarifM2: 0,
  majorationSurpiqurePaspoil: 1,
  majorationFormeComplexe: 1,
  tarifCoutureRenfortsBache: 0,
  tarifCoutureSanglesBache: 0,
  prixSangleML: 0,
  prixOeilletUnitaire: 0,
  tempsPoseOeillet: 0,
  majorationAssemblageBache: 1,
  tempsAssemblageBacheM2: 0,
  tempsPreparationBase: 0,
  tempsPreparationParM2: 0,
  tempsPreparationFermeture: 0,
  tempsPreparationMousse: 0,
  tempsPreparationMatDifferentes: 0,
};

const labels: Record<TarifsKeys, string> = {
  tarifHoraire: "Tarif horaire main d'œuvre",
  tempsCoutureMetre: 'Temps couture par mètre (min)',
  tempsCoutureGlissiere: 'Temps couture glissière (min)',
  prixFermetureGlissiere: 'Prix fermeture glissière (€)',
  tauxTVAAchat: 'Taux de TVA achat (%)',
  priseGabaritForfait: 'Prise de gabarit (forfait mini €)',
  priseGabaritTarifM2: 'Prise de gabarit (€/m²)',
  majorationSurpiqurePaspoil: 'Majoration surpiqûre/passepoil',
  majorationFormeComplexe: 'Majoration forme complexe',
  tarifCoutureRenfortsBache: 'Couture renforts bâche (€)',
  tarifCoutureSanglesBache: 'Couture sangles bâche (€)',
  prixSangleML: 'Prix sangle / ML (€)',
  prixOeilletUnitaire: 'Prix œillet unitaire (€)',
  tempsPoseOeillet: 'Temps pose œillet (min)',
  majorationAssemblageBache: 'Majoration assemblage bâche',
  tempsAssemblageBacheM2: 'Temps assemblage bâche (min/m²)',
  tempsPreparationBase: 'Temps préparation base (min)',
  tempsPreparationParM2: 'Temps préparation par m² (min)',
  tempsPreparationFermeture: 'Temps préparation fermeture (min)',
  tempsPreparationMousse: 'Temps préparation mousse (min)',
  tempsPreparationMatDifferentes: 'Temps préparation matières différentes (min)',
};

const sections: { title: string; description?: string; fields: TarifsKeys[] }[] = [
  {
    title: 'Tarifs généraux',
    description: 'Base horaire et TVA',
    fields: ['tarifHoraire', 'tauxTVAAchat'],
  },
  {
    title: 'Couture & gabarit',
    description: 'Temps et prise de gabarit',
    fields: ['tempsCoutureMetre', 'tempsCoutureGlissiere', 'priseGabaritForfait', 'priseGabaritTarifM2'],
  },
  {
    title: 'Majorations',
    description: 'Surpiqûre, forme complexe, assemblage bâche',
    fields: ['majorationSurpiqurePaspoil', 'majorationFormeComplexe', 'majorationAssemblageBache'],
  },
  {
    title: 'Bâches & sangles',
    description: 'Tarifs spécifiques bâche / sangles',
    fields: ['tarifCoutureRenfortsBache', 'tarifCoutureSanglesBache', 'prixSangleML', 'tempsAssemblageBacheM2'],
  },
  {
    title: 'Œillets',
    description: 'Prix et temps par œillet',
    fields: ['prixOeilletUnitaire', 'tempsPoseOeillet'],
  },
  {
    title: 'Préparation',
    description: 'Temps de préparation généraux',
    fields: [
      'tempsPreparationBase',
      'tempsPreparationParM2',
      'tempsPreparationFermeture',
      'tempsPreparationMousse',
      'tempsPreparationMatDifferentes',
    ],
  },
];

const TarifsCoefficients: React.FC = () => {
  const { getAuthToken, user } = useContext(AuthContext);
  const [formData, setFormData] = useState<TarifsFormData>(defaultFormData);
  const [initialFormData, setInitialFormData] = useState<TarifsFormData>(defaultFormData);
  const [seededFields, setSeededFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const baseUrl = useMemo(() => apiService.getBaseUrl(), []);

  const headers = () => buildAuthHeaders({ 'Content-Type': 'application/json' });

  const isSeeded = (key: TarifsKeys) => seededFields.includes(key);
  const highlightClass = (key: TarifsKeys) => (isSeeded(key) ? 'bg-orange-50 border-orange-200' : '');

  const computeDirty = (next: TarifsFormData) =>
    TARIFS_KEYS.some((k) => next[k] !== (initialFormData?.[k] ?? 0));

  const loadTarifs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/tarifs`, { method: 'GET', headers: headers(), credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const next: TarifsFormData = { ...defaultFormData };
      TARIFS_KEYS.forEach((k) => {
        const val = typeof data[k] === 'number' ? data[k] : defaultFormData[k];
        next[k] = val;
      });
      setFormData(next);
      setInitialFormData(next);
      setSeededFields(Array.isArray(data.seeded_fields) ? data.seeded_fields : []);
    } catch (e) {
      setError("Erreur lors du chargement des tarifs");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTarifs();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    const numeric = parseFloat(value);
    const key = name as TarifsKeys;
    const next = { ...formData, [key]: isNaN(numeric) ? 0 : numeric };
    setFormData(next);
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const dirtyFields: Partial<TarifsFormData> = {};
    TARIFS_KEYS.forEach((k) => {
      if (formData[k] !== (initialFormData?.[k] ?? 0)) {
        dirtyFields[k] = formData[k];
      }
    });
    if (Object.keys(dirtyFields).length === 0) return;

    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/tarifs`, {
        method: 'PATCH',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify(dirtyFields),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const updated = data.tarifs || data;
      const next: TarifsFormData = { ...defaultFormData };
      TARIFS_KEYS.forEach((k) => {
        const val = typeof updated[k] === 'number' ? updated[k] : formData[k];
        next[k] = val;
      });
      setFormData(next);
      setInitialFormData(next);
      setSeededFields(Array.isArray(updated.seeded_fields) ? updated.seeded_fields : []);
      setSuccess('Tarifs enregistrés avec succès');
    } catch (e) {
      setError("Erreur lors de l'enregistrement des tarifs");
      console.error(e);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleConfirm = async () => {
    if (computeDirty(formData)) return;
    setConfirmLoading(true);
    try {
      const res = await fetch(`${baseUrl}/tarifs/confirm_defaults`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const updated = data.tarifs || data;
      const next: TarifsFormData = { ...formData };
      TARIFS_KEYS.forEach((k) => {
        if (typeof updated[k] === 'number') next[k] = updated[k];
      });
      setFormData(next);
      setInitialFormData(next);
      setSeededFields([]);
      setSuccess('Valeurs confirmées');
    } catch (e) {
      setError('Erreur lors de la confirmation');
      console.error(e);
    } finally {
      setConfirmLoading(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const isDirty = computeDirty(formData);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des tarifs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-medium text-gray-900">Tarifs et coefficients</h2>
          <p className="text-sm text-gray-600">Champs orange = valeurs par défaut non confirmées.</p>
        </div>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isDirty || confirmLoading}
          title={isDirty ? 'Enregistrez d’abord vos modifications' : ''}
          className={`px-4 py-2 rounded-md border text-sm font-medium ${
            isDirty
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
          }`}
        >
          {confirmLoading ? 'Validation...' : '✅ Ces valeurs sont correctes'}
        </button>
      </div>

      {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">{success}</div>}
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-5">
          {sections.map((section) => (
            <div key={section.title} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
                  {section.description && <p className="text-sm text-gray-600">{section.description}</p>}
                </div>
                <div className="text-xs text-gray-400">Champs orange = non confirmés</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((key) => (
                  <div key={key}>
                    <label className="form-label flex items-center justify-between">
                      <span>{labels[key]}</span>
                      {isSeeded(key) && (
                        <span className="ml-2 text-[11px] font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                          Par défaut
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      step="0.01"
                      className={`input-field ${highlightClass(key)}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={loading}>
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
};

export default TarifsCoefficients;
